import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToolsService } from 'src/app/services/tools.service';

interface CustomField {
  field_name: string;
  field_label: string;
  field_type: string;
  field_condition?: any;
  options?: any[];
  is_required?: boolean;
  control?: FormControl;
  levels?: DynamicLevel[];
  question?: any;
}

interface DynamicLevel {
  label: string;
  options: any[];
  filtered_options: any[];
  control: FormControl;
  searchTerm?: string;
}

interface CustomStep {
  id: number;
  order: number;
  step_type?: string;
  fields: CustomField[];
  questions: any[];
}

@Component({
  selector: 'app-customsignup',
  templateUrl: './customsignup.component.html',
  styleUrls: ['./customsignup.component.scss'],
})
export class CustomsignupComponent implements OnInit, OnChanges {
  @Input() data: any;
  @Input() defaultQuestions: any[];
  @Output() submitForm = new EventEmitter<any>();

  constructor(private toolsService: ToolsService) { }

  steps: CustomStep[] = [];
  currentStepIndex = 0;
  form: FormGroup = new FormGroup({});
  /** Map of control name to fields that depend on it */
  private dependencies: { [key: string]: CustomField[] } = {};

  ngOnInit() {
    if (this.data) {
      this.initializeForm();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data && changes.data.currentValue) {
      this.initializeForm();
    }
    if (changes.defaultQuestions && changes.defaultQuestions.currentValue && this.steps.length === 0) {
      this.initializeForm();
    }
  }

  private isCurrentStepValid(): boolean {
    const step = this.currentStep;
    if (!step) {
      return false;
    }
    if (step.step_type === 'default_step') {
      if (!this.toolsService.validateQuestions(this.defaultQuestions)) {
        this.toolsService.markAsTouched(this.defaultQuestions);
        return false;
      }
      return true;
    }
    let valid = true;
    step.fields.forEach(f => {
      if (!this.showField(f)) {
        return;
      }

      if (f.levels) {
        f.levels.forEach(level => {
          if(level.control.invalid) valid = false
        })
      } else if (f.control.invalid) valid = false;

    });
    if (!valid) {
      step.fields.forEach(f => {
        if (this.showField(f)) {
          f.control.markAsTouched();
        }
      });
    }
    return valid;
  }

  /** Build steps and form controls */
  initializeForm() {
    this.form = new FormGroup({});
    this.dependencies = {};
    this.currentStepIndex = 0;
    this.steps = (this.data.steps || [])
      .sort((a, b) => a.order - b.order)
      .map((step: any) => this.buildStep(step));

    this.steps.forEach(step => {
      if (step.step_type !== 'default_step') {
        step.fields.forEach(field => {
          if (field.levels) {
            field.levels.forEach(level => {
              if (field.field_name === level.label ) {
                this.form.addControl(field.field_name, level.control);
                if (field.field_condition) {
                  for (const key of Object.keys(field.field_condition)) {
                    if (!this.dependencies[key]) {
                      this.dependencies[key] = [];
                    }
                    this.dependencies[key].push(field);
                  }
                }
              }
            })
          } else {
            this.form.addControl(field.field_name, field.control);
            if (field.field_condition) {
              for (const key of Object.keys(field.field_condition)) {
                if (!this.dependencies[key]) {
                  this.dependencies[key] = [];
                }
                this.dependencies[key].push(field);
              }
            }
          }
        });
      }
    });

    Object.keys(this.dependencies).forEach(dep => {
      const control = this.form.get(dep);
      control.valueChanges.subscribe(() => this.handleFieldChange(dep));
    });
  }

  buildStep(stepData: any): CustomStep {
    const fields: CustomField[] = (stepData.fields || [])
      .sort((a, b) => a.order - b.order)
      .map((f: any) => this.buildField(f));
    const questions = fields.map(f => f.question);
    return { id: stepData.id, order: stepData.order, step_type: stepData.step_type, fields, questions };
  }

  buildField(fieldData: any): CustomField {
    const validators = fieldData.is_required ? [Validators.required] : [];
    const control = new FormControl('', validators);
    const field: CustomField = {
      field_name: fieldData.field_name,
      field_label: fieldData.field_label,
      field_type: fieldData.field_type,
      field_condition: fieldData.field_condition,
      options: fieldData.options,
      is_required: fieldData.is_required,
      control,
    };

    if (field.field_type === 'select' && Array.isArray(fieldData.options)) {
      field.options = fieldData.options.map((opt: any) => ({
        name: opt.name || opt.label,
        value: opt.value,
      }));
    }

    if (field.field_type === 'dynamic_select' && Array.isArray(fieldData.options)) {
      field.levels = fieldData.options.map((level: any) => {
        const options = Array.isArray(level.options)
          ? level.options.map((opt: any) => ({
              name: opt.name || opt.label,
              value: opt.value,
              condition: opt.condition,
            }))
          : [];
        return {
          label: level.label,
          options,
          filtered_options: options,
          control: new FormControl(''),
          searchTerm: ''
        } as DynamicLevel;
      });
    }

    field.question = this.buildQuestion(field);
    return field;
  }

  buildQuestion(field: CustomField) {
    const question: any = {
      label: field.field_label,
      formControl: field.control
    };

    switch (field.field_type) {
      case 'text':
        question.format = 'input';
        break;
      case 'select':
        question.format = 'select';
        question.options = field.options;
        break;
      case 'dynamic_select':
        question.format = 'dynamic_select';
        question.levels = field.levels;
        break;
      default:
        question.format = 'input';
        break;
    }
    return question;
  }

  get currentStep(): CustomStep {
    return this.steps[this.currentStepIndex];
  }

  get currentQuestions() {
    if (!this.currentStep) return [];
    return this.currentStep.fields.filter(field => this.showField(field)).map(field => field.question);
  }

  /** Determine if field should be displayed based on conditions */
  showField(field: CustomField): boolean {
    if (!field.field_condition || Object.keys(field.field_condition).length === 0) {
      return true;
    }
    for (const key of Object.keys(field.field_condition)) {
      const control = this.form.get(key);
      if (!control) {
        continue;
      }
      const allowed = field.field_condition[key];
      if (allowed.indexOf(control.value) === -1) {
        return false;
      }
    }
    return true;
  }

  /** Reset dependent fields when a parent control changes */
  private handleFieldChange(controlName: string) {
    const dependents = this.dependencies[controlName] || [];
    dependents.forEach(dep => {
      if (!this.showField(dep)) {
        dep.control.setValue('');
        if (dep.levels) {
          dep.levels.forEach(l => l.control.setValue(''));
        }
        this.handleFieldChange(dep.field_name);
      }
    });
  }

  /** Validate all visible fields across the form */
  private isFormValid(): boolean {
    if (!this.toolsService.validateQuestions(this.defaultQuestions)) {
      return false;
    }
    for (const step of this.steps) {
      for (const field of step.fields) {
        if (field.levels) {
        field.levels.forEach(level => {
          if (level.control.invalid) {
            return false
          }
        })
      } else if (this.showField(field) && field.control.invalid) {
          return false;
        }
      }
    }
    return true;
  }

  /** Mark all visible controls as touched */
  private markVisibleControlsAsTouched() {
    this.steps.forEach(step => {
      step.fields.forEach(field => {
        if (this.showField(field)) {
          field.control.markAsTouched();
          if (field.levels) {
            field.levels.forEach(l => l.control.markAsTouched());
          }
        }
      });
    });
    this.toolsService.markAsTouched(this.defaultQuestions);
  }


  nextStep() {
    console.debug('Submitting form with data:', this.showDuratexRepLegalTitle);
    // console.log("currentQuestions ->", this.currentQuestions) Use this line for debugging.
    for (let question of this.currentQuestions) {
      if (question.format === "dynamic_select") {
        for (const level of question.levels) {
          const exists = level.options.some(item => item.name.toUpperCase() === level.searchTerm);
          if (level.searchTerm === "") {
            this.toolsService.showErrorToast("El campo " + level.label + " es obligatorio")
            return; // Exits if searchTerm is empty.
          }
          if (!exists) {
            this.toolsService.showErrorToast("El valor " + level.searchTerm + " no coincide con ningúna opción valida de " + level.label)
            return;
          }
        }
      }
    }

    if (!this.isCurrentStepValid()) {
      return;
    }
    if (this.currentStepIndex < this.steps.length - 1) {
      this.currentStepIndex++;
    } else {
      this.submit();
    }
  }

  get showDuratexRepLegalTitle(){
    console.debug('Checking for Duratex Representative Legal Title', this.form.value);
    let ocupacion = this.form.value['Ocupación'];
    return ocupacion && ocupacion.toLowerCase() === 'carpintero persona jurídica';
  }

  prevStep() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
    }
  }

  submit() {
    if (!this.isCurrentStepValid()) return;
    if (!this.isFormValid()) {
      this.markVisibleControlsAsTouched();
      return;
    }
    const defaultValues = this.defaultQuestions.map(q => ({
      label: q.label,
      value: q.formControl ? q.formControl.value : null
    })) || [];
    this.submitForm.emit({ custom: this.form.value, default: defaultValues });
  }
}

